import { Component, Input } from '@angular/core';
import {
  ProjectAnalysisReport,
  FileAnalysisReport
} from '../../../../core/models/analysis-report';
import { AnalysisExportService } from '../../services/analysis-export.service';
import { AnalysisScoringService } from '../../services/analysis-scoring.service';

interface CombinedIssue {
  id: string;
  type: 'Violation' | 'Code Smell' | 'Vulnerability';
  severity: string;
  line: number | null;
  description: string;
  codeFragment: string;
  suggestion: string;
}

const SEVERITY_ORDER: Record<string, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MAJOR: 2,
  MEDIUM: 3,
  MINOR: 4,
  LOW: 5,
  INFO: 6
};

@Component({
  selector: 'app-analysis-dashboard',
  standalone: false,
  templateUrl: './analysis-dashboard.component.html',
  styleUrls: ['./analysis-dashboard.component.scss']
})
export class AnalysisDashboardComponent {

  @Input() report!: ProjectAnalysisReport;
  @Input() taskId!: string;

  selectedFileIndex = 0;
  isExporting = false;

  constructor(
    private exportService: AnalysisExportService,
    private scoringService: AnalysisScoringService
  ) {}

  getRating(score: number): string {
    return this.scoringService.getRating(score);
  }

  getRatingClass(score: number): string {
    return `rating--${this.getRating(score).toLowerCase()}`;
  }

  getSeverityClass(severity: string): string {
    return `severity--${(severity || '').toLowerCase()}`;
  }

  formatScore(score: number): string {
    return this.scoringService.formatScore(score);
  }

  get showCleanCodeCard(): boolean {
    return !this.report.analysisType || this.report.analysisType === 'CLEAN_CODE';
  }

  get showCodeSmellsCard(): boolean {
    return !this.report.analysisType || this.report.analysisType === 'CODE_SMELLS';
  }

  get showSecurityCard(): boolean {
    return !this.report.analysisType || this.report.analysisType === 'SECURITY';
  }

  get cleanCodeScore(): number {
    return this.scoringService.cleanCodeScore(this.report?.summary);
  }

  get codeSmellsScore(): number {
    return this.scoringService.codeSmellsScore(this.report?.summary);
  }

  get securityScore(): number {
    return this.scoringService.securityScore(this.report?.summary);
  }

  get qualityGatePassed(): boolean {
    return this.scoringService.qualityGatePassed(this.report);
  }

  get selectedFileReport(): FileAnalysisReport | null {
    return this.report?.fileReports?.[this.selectedFileIndex] ?? null;
  }

  get combinedIssues(): CombinedIssue[] {
    const file = this.selectedFileReport;
    if (!file) return [];

    const issues: CombinedIssue[] = [];

    if (file.cleanCodeAnalysis?.violations) {
      for (const v of file.cleanCodeAnalysis.violations) {
        issues.push({
          id: v.id,
          type: 'Violation',
          severity: v.severity,
          line: v.line,
          description: v.description,
          codeFragment: v.codeFragment,
          suggestion: v.suggestion
        });
      }
    }

    if (file.codeSmellDetection?.smells) {
      for (const s of file.codeSmellDetection.smells) {
        issues.push({
          id: s.id,
          type: 'Code Smell',
          severity: s.severity,
          line: s.line,
          description: s.description,
          codeFragment: s.codeFragment,
          suggestion: s.suggestion
        });
      }
    }

    if (file.securityScan?.vulnerabilities) {
      for (const v of file.securityScan.vulnerabilities) {
        issues.push({
          id: v.id,
          type: 'Vulnerability',
          severity: v.severity,
          line: v.line,
          description: v.description,
          codeFragment: v.codeFragment,
          suggestion: v.remediation
        });
      }
    }

    issues.sort((a, b) =>
      (SEVERITY_ORDER[a.severity?.toUpperCase()] ?? 99)
      - (SEVERITY_ORDER[b.severity?.toUpperCase()] ?? 99)
    );

    return issues;
  }

  selectFile(index: number): void {
    this.selectedFileIndex = index;
  }

  async exportToPdf(): Promise<void> {
    this.isExporting = true;
    try {
      await this.exportService.exportToPdf(this.report);
    } finally {
      this.isExporting = false;
    }
  }

  async exportToHtml(): Promise<void> {
    this.isExporting = true;
    try {
      await this.exportService.exportToHtml(this.taskId);
    } finally {
      this.isExporting = false;
    }
  }

  async exportToExcel(): Promise<void> {
    this.isExporting = true;
    try {
      await this.exportService.exportToExcel(this.report);
    } finally {
      this.isExporting = false;
    }
  }
}
